import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { switchMap, timeout } from 'rxjs/operators';

import { CookieService } from './cookie.service';
import { ApiRequestService } from './api-request.service';
export class AnalyticsService {
private enabled: boolean;
private basicAuth: any;
private warningMessage: string;
private userIdCookieName: string;
private dueTime: number;
private mobileSiteSubdomains: Array<string>;
/**
 * @param {?} cookieService
 * @param {?} apiRequestService
 */
constructor(
private cookieService: CookieService,
private apiRequestService: ApiRequestService,
  ) {
    this.enabled = true;
    this.basicAuth = 'Basic bWt0LXVzZXI6WHlWYm5BcXdl';
    this.warningMessage = `Tracking is DISABLED! Change 'enabled' var in AnalyticsService`;
    this.userIdCookieName = 'TNK-GUEST-ID';
    this.dueTime = 5000;
    this.mobileSiteSubdomains = ['m-dev', 'm', 'mtest-new'];
  }
/**
 * @param {?} value
 * @return {?}
 */
private isValueExist(value: number): boolean {
    return value > -1 ? true : false;
  }
/**
 * @param {?=} body
 * @return {?}
 */
private createHttpOptions(body: any = {}): Object {
    return {
      headers: new HttpHeaders(body),
    };
  }
/**
 * @return {?}
 */
private getUserId(): Observable<any> {
    const /** @type {?} */ headers = this.createHttpOptions({
      Authorization: this.basicAuth,
    });
    return this.apiRequestService.get('/userid', headers);
  }
/**
 * Send POST data to url '/leads'
 * @param {?} userId
 * @param {?=} body
 * @return {?} Observable
 */
private sendDropOffLeads(userId: string, body: Object = {}): Observable<any> {
    const /** @type {?} */ headers = this.createHttpOptions({
      'Content-Type': 'application/json',
      'user-token': userId,
    });
    return this.apiRequestService.post('/leads', body, headers);
  }
/**
 * Check if userId in Cookie exists
 * if NOT EXIST return Observable getUserId()
 * Otherwise, return Observable with CURRENT cookieValue
 * @return {?} Observable
 */
private checkUserId(): Observable<any> {
    if (this.enabled) {
      const /** @type {?} */ cookieValue = this.cookieService.get(this.userIdCookieName);
      const /** @type {?} */ userIdResponse$ = this.buildUserIdResponse(cookieValue);
      return userIdResponse$;
    }
    // tslint:disable-next-line
    console.log(`%c${this.warningMessage}`, 'color: orange; font-weight: bold');
  }
/**
 * @param {?} cookieValue
 * @return {?}
 */
private buildUserIdResponse(cookieValue: any): Observable<any> {
    if (cookieValue === undefined) {
      return this.getUserId(); // fetch userId again
    } else {
      // Create response Object that has the same format as this.getUserId()
      const /** @type {?} */ resObj = {
        data: {
          user_id: cookieValue,
        },
      };
      return of(resObj); // Observable of with userId cookie value
    }
  }
/**
 * Returns Object that contains Array of Form Inputs each form step
 * @return {?}
 */
private inputFieldConstants(): Object {
    const /** @type {?} */ slider1 = ['loanAmount', 'loanPeriodInMonths', 'refcode'];
    const /** @type {?} */ slider2 = [
      ...slider1,
      'firstName',
      'lastName',
      'mobilePhoneNumber',
      'emailAddress',
      'emailIsOwn',
      'loanPurposeType',
      'mediasource',
    ];
    const /** @type {?} */ form1 = [...slider2, 'pin', 'account', 'educationType'];
    const /** @type {?} */ form2 = [...form1, 'birthCityId', 'dateOfBirth', 'gender'];
    const /** @type {?} */ form3 = [
      ...form2,
      'domicileAddress',
      'domicileAddressObj',
      'religion',
      'citizenship',
    ];
    const /** @type {?} */ form4 = [...form3, 'housing', 'dependants', 'mothersName'];
    const /** @type {?} */ form5 = [...form4, 'contactAddress'];
    const /** @type {?} */ form6 = [...form5, 'income'];
    const /** @type {?} */ form7 = [...form6, 'maritalStatusType', 'spouse'];
    const /** @type {?} */ form8 = [...form7, 'personalReference'];
    const /** @type {?} */ form9 = [...form8]; // Ubah Data section
    const /** @type {?} */ form10 = [...form8, 'finished_form']; // Final step

    return {
      slider1,
      slider2,
      form1,
      form2,
      form3,
      form4,
      form5,
      form6,
      form7,
      form8,
      form9,
      form10,
    };
  }
/**
 * Returns related form inputs for related form steps
 * @param {?} formData
 * @param {?} formStep
 * @return {?}
 */
private getRelatedFormFields(formData: Object, formStep: string): Object {
    const /** @type {?} */ formProperties = this.inputFieldConstants(); // Get a list of Form Input Objects
    const /** @type {?} */ formInputArray = formProperties[formStep]; // ['loanAmount', 'firstName', 'lastName']
    const /** @type {?} */ dropOffPage = window.location.href;
    const /** @type {?} */ baseObject = {
      sourcePlatform: this.getWebsitePlatform(),
      dropOffPage,
    };

    // Filter ['maritalStatusType', 'spouse'] array that
    // exists in Object keys {'maritalStatusType': 'Divorced', 'gender': 'Male'}
    // then, assign baseObject with {'maritalStatusType': formData['maritalStatusType']}, etc.
    return formInputArray
      .filter((formInput: string) =>
        this.isValueExist(Object.keys(formData).indexOf(formInput)),
      )
      .reduce((newObj: Object, key: string) => {
        return {
          ...newObj,
          [key]: formData[key],
        };
      }, baseObject);
  }
/**
 * @param {?} cookieName
 * @return {?}
 */
private getRefcodeValue(cookieName: string): any {
    const /** @type {?} */ cookieValue = this.cookieService.get(cookieName);
    if (cookieValue) {
      return `${cookieValue}`.toUpperCase();
    }

    return cookieValue;
  }
/**
 * @param {?} sliderData
 * @return {?}
 */
private transformSliderFields(sliderData: any): Object {
    const /** @type {?} */ refcodeCookieName = 'TNK-REF';
    const /** @type {?} */ refcode = this.getRefcodeValue(refcodeCookieName);

    return {
      ...(refcode && { refcode }), // add 'refcode' as a member if it has a value
      ...sliderData,
      loanPeriodInMonths:
        sliderData.loanPeriod || sliderData.loanPeriodInMonths,
      loanPurposeType: sliderData.loanPurpose || sliderData.loanPurposeType,
    };
  }
/**
 * @return {?}
 */
private getWebsitePlatform(): string {
    const /** @type {?} */ currentSubdomain = window.location.hostname.split('.')[0];
    const /** @type {?} */ isMobileSite = this.isValueExist(
      this.mobileSiteSubdomains.indexOf(currentSubdomain),
    );
    return isMobileSite ? 'Mobile Site' : 'Desktop Site';
  }
/**
 * @param {?=} transformedLoanData
 * @return {?}
 */
public addFinishedFormFlag(transformedLoanData: Object = {}): Object {
    return {
      ...transformedLoanData,
      finished_form: true,
    };
  }
/**
 * @param {?} body
 * @return {?}
 */
public trackEvent(body: Object): void {
    const /** @type {?} */ trackUser$ = this.checkUserId().pipe(
      switchMap(res => {
        this.cookieService.set(this.userIdCookieName, res.data.user_id);
        return this.sendDropOffLeads(res.data.user_id, body);
      }),
    );

    trackUser$.pipe(timeout(this.dueTime)).subscribe();
  }
/**
 * @param {?} formData
 * @param {?} formStep
 * @return {?}
 */
public trackFormEvent(formData: Object, formStep: string): void {
    const /** @type {?} */ transformedSlider = this.transformSliderFields(formData);
    const /** @type {?} */ body = this.getRelatedFormFields(transformedSlider, formStep);

    this.trackEvent(body);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: CookieService, },
{type: ApiRequestService, },
];
}

function AnalyticsService_tsickle_Closure_declarations() {
/** @type {?} */
AnalyticsService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
AnalyticsService.ctorParameters;
/** @type {?} */
AnalyticsService.prototype.enabled;
/** @type {?} */
AnalyticsService.prototype.basicAuth;
/** @type {?} */
AnalyticsService.prototype.warningMessage;
/** @type {?} */
AnalyticsService.prototype.userIdCookieName;
/** @type {?} */
AnalyticsService.prototype.dueTime;
/** @type {?} */
AnalyticsService.prototype.mobileSiteSubdomains;
/** @type {?} */
AnalyticsService.prototype.cookieService;
/** @type {?} */
AnalyticsService.prototype.apiRequestService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
